import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, Handshake, User, Rocket, ShieldCheck, Globe } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import FinalCTA from "@/sections/FinalCTA";
import { SERVICES } from "@/lib/services-data";

const iconMap = { Building2, Handshake, User, Rocket, ShieldCheck, Globe };

const ServicesHeroSlot = ({ services }) => (
 <div className="relative">
 <div className="absolute -top-5 -right-5 w-32 h-32 rounded-full bg-[#FDD805] opacity-90 -z-0" aria-hidden />
 <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-2xl border-2 border-[#0F1115] -z-0" aria-hidden />
 <div className="relative bg-white border border-[#EAEAEA] rounded-3xl p-6 shadow-[0_10px_40px_rgba(0,0,0,0.05)] z-10">
 <div className="flex items-center justify-between mb-5">
 <div>
 <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A]">
 Our services
 </div>
 <div className="mt-1 font-display font-black text-[15px] text-[#0F1115] leading-tight">
 6 pillars · one launch
 </div>
 </div>
 <Link
 to="/services"
 className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] font-bold text-[#0F1115] hover:gap-2 transition-[gap]"
 >
 View all <ArrowUpRight size={12} />
 </Link>
 </div>

 <div className="grid grid-cols-2 gap-2.5">
 {(services || []).slice(0, 6).map((s, i) => {
 const Icon = iconMap[s.icon] || Building2;
 const short = s.title
 .replace(" Registration", "")
 .replace("Startup India / DPIIT Support", "Startup India / DPIIT")
 .replace("GST / MSME / Compliance", "GST · MSME · ROC")
 .replace("Business Presence Setup", "Business Presence");
 const palette = [
 { bg: "#FEF6C7", text: "#7A5B00" }, // yellow
 { bg: "#D1FAE5", text: "#065F46" }, // mint
 { bg: "#DBEAFE", text: "#1E3A8A" }, // sky
 { bg: "#FCE7F3", text: "#9D174D" }, // pink
 { bg: "#FED7AA", text: "#7C2D12" }, // coral
 { bg: "#E9D5FF", text: "#5B21B6" }, // lavender
 ][i % 6];
 return (
 <Link
 key={s.slug}
 to={`/services/${s.slug}`}
 className="group flex items-center gap-3 rounded-2xl border border-[#EAEAEA] bg-white hover:border-[#0F1115] hover:shadow-[0_6px_20px_rgba(0,0,0,0.06)] p-3 transition-all"
 data-testid={`services-hero-tile-${s.slug}`}
 >
 <div
 className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-105"
 style={{ backgroundColor: palette.bg }}
 >
 <Icon size={18} style={{ color: palette.text }} />
 </div>
 <div className="min-w-0 flex-1">
 <div className="font-display font-bold text-[13px] text-[#0F1115] leading-tight line-clamp-2">
 {short}
 </div>
 </div>
 </Link>
 );
 })}
 </div>
 </div>
 </div>
);

export default function ServicesPage() {
 const services = SERVICES;
 return (
 <div data-testid="page-services">
 <Seo
  title="Company, LLP & GST Registration Services in Mumbai"
  description="Pvt Ltd, LLP and OPC registration, Startup India DPIIT, GST, MSME and ROC compliance, plus domain, email and website setup for new founders."
  path="/services"
  breadcrumbs={[
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
  ]}
 />
 <PageHero
 eyebrow="Services"
 title="Everything a First-Time Founder needs to start right."
 subtitle="From choosing the right structure to registrations, compliance and business presence we help you build the right foundation from day one."
 rightSlot={<ServicesHeroSlot services={services} />}
 />
 <section className="bg-white">
 <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
 {services.map((s, i) => {
 const Icon = iconMap[s.icon] || Building2;
 return (
 <Reveal delay={i * 60} key={s.slug}>
 <Link
 to={`/services/${s.slug}`}
 className="group block h-full bg-white border border-[#EAEAEA] rounded-2xl p-7 hover:border-[#0F1115] transition-colors relative overflow-hidden flex flex-col items-center text-center"
 data-testid={`services-page-card-${s.slug}`}
 >
 <div className="absolute top-0 left-0 h-1 w-0 bg-[#FDD805] group-hover:w-full transition-[width] duration-500" />
 <div className="w-11 h-11 rounded-xl bg-[#FEF6C7] flex items-center justify-center">
 <Icon size={20} className="text-[#0F1115]" />
 </div>
 <h3 className="mt-5 font-display text-[22px] font-bold text-[#0F1115]">
 {s.title}
 </h3>
 <p className="mt-2 text-[15px] text-[#1C1F26] leading-relaxed">
 {s.short}
 </p>
 <div className="mt-5 text-[13px] font-bold text-[#0F1115] inline-flex items-center gap-1.5">
 View details <ArrowUpRight size={14} />
 </div>
 </Link>
 </Reveal>
 );
 })}
 </div>
 </section>
 <FinalCTA />
 </div>
 );
}
